import React from 'react';
import { defineMessages, FormattedMessage, type MessageDescriptor } from 'react-intl';
import { StyleSheet } from 'react-native';

import { BodyText, theme } from '@sg/garnish';

import { type OrderChannel } from '../../../machine/ordering-machine.types';

export const BagAnimatedDialogTitle = (props: BagAnimatedDialogTitleProps) => {
  const {
    orderChannel,
    isFlexEnabled,
    isShowingCheckoutLedger,
    isChangingTime,
    isChangingDeliveryPreferences,
    isChangingPaymentMethod,
    isShowingGiftCardForm,
    isShowingLoyaltyInfo,
  } = props;

  const isShowingFlexCheckout = isShowingCheckoutLedger && isFlexEnabled;

  return (
    <BodyText
      style={styles.title}
      sizeMatch={[isShowingFlexCheckout ? '18' : '22']}
      bold={isShowingFlexCheckout}
    >
      {isShowingCheckoutLedger && !isFlexEnabled ? (
        <FormattedMessage {...messages.checkout} />
      ) : null}

      {isShowingFlexCheckout ? <FormattedMessage {...orderChannelMessages[orderChannel]} /> : null}

      {isChangingTime ? <FormattedMessage {...messages.chooseTime} /> : null}

      {isChangingDeliveryPreferences ? (
        <FormattedMessage {...messages.editDeliveryPreferences} />
      ) : null}

      {isChangingPaymentMethod ? <FormattedMessage {...messages.selectPaymentMethod} /> : null}

      {isShowingGiftCardForm ? <FormattedMessage {...messages.redeemGiftCard} /> : null}

      {isShowingLoyaltyInfo ? <FormattedMessage {...messages.loyaltyInfo} /> : null}
    </BodyText>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  title: {
    lineHeight: theme.spacing['8'],
    textAlign: 'center',
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  checkout: {
    defaultMessage: 'Checkout',
    description: 'Checkout | Checkout | Title',
  },
  pickupCheckout: {
    defaultMessage: 'Pickup Checkout',
    description: 'Checkout | Pickup Checkout | Title',
  },
  deliveryCheckout: {
    defaultMessage: 'Delivery Checkout',
    description: 'Checkout | Delivery Checkout | Title',
  },
  outpostCheckout: {
    defaultMessage: 'Outpost Checkout',
    description: 'Checkout | Outpost Checkout | Title',
  },
  chooseTime: {
    defaultMessage: 'Time',
    description: 'Checkout | Choose a time | Title',
  },
  editDeliveryPreferences: {
    defaultMessage: 'Delivery',
    description: 'Checkout | Edit delivery preferences | Title',
  },
  selectPaymentMethod: {
    defaultMessage: 'Payments',
    description: 'Checkout | Select payment method | Title',
  },
  redeemGiftCard: {
    defaultMessage: 'Redeem',
    description: 'Checkout | Redeem gift card | Title',
  },
  loyaltyInfo: {
    defaultMessage: 'Benefits',
    description: 'Checkout | Loyalty info | Title',
  },
});

const orderChannelMessages: Record<OrderChannel, MessageDescriptor> = {
  pickup: messages.pickupCheckout,
  delivery: messages.deliveryCheckout,
  outpost: messages.outpostCheckout,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagAnimatedDialogTitleProps = {
  orderChannel: OrderChannel;
  isFlexEnabled?: boolean;
  isShowingCheckoutLedger?: boolean;
  isChangingTime?: boolean;
  isChangingDeliveryPreferences?: boolean;
  isChangingPaymentMethod?: boolean;
  isShowingGiftCardForm?: boolean;
  isShowingLoyaltyInfo?: boolean;
};
